import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {AuthService} from '@security/service/auth.service';
import {RefreshPayload} from '@security/model';
import {ApiResponse, ApiUriEnum, ToastType} from '@shared/model';
import {ToasterService} from "@shared/service/toaster.service";

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {
  attemps = 0;

  constructor(public auth: AuthService, public toaster: ToasterService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const cloneReq = this.addToken(req);
    return next.handle(cloneReq).pipe(
      catchError((err: HttpErrorResponse) => {
        return this.handleError(err, cloneReq, next)
      })
    )
  }

  private addToken(req: HttpRequest<any>): HttpRequest<any> {
    if (
      !req.url.includes(ApiUriEnum.SIGNIN) &&
      !req.url.includes(ApiUriEnum.SIGNUP) &&
      !req.url.includes(ApiUriEnum.VERIFY_OTP) &&
      !req.url.includes(ApiUriEnum.ACTIVATE_2FA) &&
      !req.url.includes(ApiUriEnum.REFRESH_TOKEN) &&
      !req.url.includes(ApiUriEnum.HELLO_WORLD)
    ) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${this.auth.tokenService.getToken()}`
        }
      });
    }
    return req;
  }

  private handleError(err: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (this.attemps > 1) {
      this.attemps = 0;
      this.auth.navigation.navigateToUnsecure();
      return of({result: false, data: null, code: 'api.fatal-error'});
    }

    this.attemps++;

    if (err.error.error === 'unauthorized' || err.status === 401) {
      if (this.auth.tokenService.getRefreshToken() == null) {
        this.auth.navigation.navigateToUnsecure();
        return of({result: false, data: null, code: 'api.fatal-error'});
      } else {
        const refreshPayload: RefreshPayload = {
          refresh: this.auth.tokenService.getRefreshToken()!
        };

        return this.auth.refreshToken(refreshPayload).pipe(switchMap((response: ApiResponse) => {
          if (!response.result) {
            this.auth.logout();
            this.auth.navigation.navigateToUnsecure();
            return of({result: false, data: null, code: 'api.fatal-error'});
          }

          const cloneReq = this.addToken(req);
          return next.handle(cloneReq).pipe(
            catchError((err: HttpErrorResponse) => {
              return this.handleError(err, req, next)
            })
          )
        }));
      }
    } else if(err.error.error === 'forbidden' || err.status === 403) {
      // URLs qui peuvent échouer silencieusement (données optionnelles)
      const silentUrls = [
        '/api/leave/nopagelist',
        '/api/prestationType/listNoPagination', 
        '/api/prestationGroup/listNoPagination',
        '/api/employee/nopagelist',
        '/api/employee/detail'
      ];
      
      const isSilentUrl = silentUrls.some(url => req.url.includes(url));
      
      if (isSilentUrl) {
        // Retourner une réponse vide pour les données optionnelles
        return of({result: false, data: [], error: 'access_denied', code: 'api.error-403'});
      } else {
        // Afficher la popup seulement pour les erreurs importantes
        this.toaster.display({type: ToastType.ERROR, body: 'api.error-403'});
        return of({result: false, data: null, error: 'access_denied', code: 'api.error-403'});
      }
    }

    throw err;
  }
}
